import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import {
  familyOf,
  parseInstrumentName,
  type AssessmentId,
  type FinishedAssessment,
} from "@thalamos/haven-common";

/**
 * An earlier assessment a summary may be compared with: a definitive one,
 * approved, or a contribution, completed with no approval needed.
 */
export type ComparisonCandidate = {
  assessmentId: AssessmentId;
  /** When it was approved, or completed. */
  finishedAt: Date;
} & (
  | {
      kind: "definitive";
      /** Whether it is the latest definitive one: the one just before. */
      justBefore: boolean;
      /** Whether it is the definitive one nearest a year before, where one is within `YEAR_AGO_SLACK_DAYS` of it. */
      yearAgo: boolean;
    }
  | {
      kind: "contribution";
      /** Whose it is, or undefined where that is not known. */
      assessor: string | undefined;
    }
);

/**
 * How far from a year before an assessment may be and still be the one a
 * year ago: a season either side, since reviews fall due yearly but are
 * rarely held on the day.
 */
const YEAR_AGO_SLACK_DAYS = 92;

/** The family a rendered name belongs to, or undefined for one that does not parse. */
export const familyOfName = (name: string): string | undefined => {
  const parsed = parseInstrumentName(name);

  return parsed.success ? familyOf(parsed.data) : undefined;
};

/**
 * The finished assessments a summary may be compared with, most recent first:
 * those of the same young person approved or completed before `before`, on an
 * instrument of the same family, whose answers this session may read. The
 * one before and the one a year ago are picked from the definitive ones
 * alone, those being the record of what was agreed.
 *
 * The same family because an item key means one thing only within its family,
 * which is what lets an item be matched across two versions.
 */
export const comparisonCandidates = ({
  history,
  assessmentId,
  instrumentName,
  before,
  mayRead,
  assessorOf,
}: {
  history: readonly FinishedAssessment[];
  /** The assessment being summarised, which is never its own candidate. */
  assessmentId: AssessmentId;
  instrumentName: string;
  before: Date;
  mayRead: (assessment: FinishedAssessment) => boolean;
  /** Whose an assessment is, as far as it is known. */
  assessorOf: (assessment: FinishedAssessment) => string | undefined;
}): ComparisonCandidate[] => {
  const family = familyOfName(instrumentName);

  if (family === undefined) {
    return [];
  }

  const earlier = history
    .filter(
      (assessment) =>
        assessment.id !== assessmentId &&
        assessment.finishedAt < before &&
        familyOfName(assessment.instrument.name) === family &&
        mayRead(assessment),
    )
    .toSorted(
      (left, right) => right.finishedAt.getTime() - left.finishedAt.getTime(),
    );
  const definitive = earlier.filter(({ status }) => status === "approved");
  const yearAgo = nearestYearBefore({ assessments: definitive, before });

  return earlier.map((assessment): ComparisonCandidate =>
    match(assessment.status)
      .with("approved", () => ({
        kind: "definitive" as const,
        assessmentId: assessment.id,
        finishedAt: assessment.finishedAt,
        justBefore: assessment === definitive[0],
        yearAgo: assessment === yearAgo,
      }))
      .with("completed", () => ({
        kind: "contribution" as const,
        assessmentId: assessment.id,
        finishedAt: assessment.finishedAt,
        assessor: assessorOf(assessment),
      }))
      .exhaustive(),
  );
};

const nearestYearBefore = ({
  assessments,
  before,
}: {
  assessments: readonly FinishedAssessment[];
  before: Date;
}): FinishedAssessment | undefined => {
  const yearBefore = dayjs(before).subtract(1, "year");
  const awayFrom = ({ finishedAt }: FinishedAssessment) =>
    Math.abs(dayjs(finishedAt).diff(yearBefore, "day", true));

  return assessments
    .filter((assessment) => awayFrom(assessment) <= YEAR_AGO_SLACK_DAYS)
    .toSorted((left, right) => awayFrom(left) - awayFrom(right))[0];
};
